import { useEffect, type RefObject } from "react";

/**
 * Accessible focus management for a modal dialog.
 *
 * On mount (when `active`): captures the currently-focused element, then moves
 * focus into `containerRef` — to its first focusable descendant, or the
 * container itself (which must have `tabIndex={-1}`) when it has none.
 *
 * While `active` and `trapEnabled`: Tab / Shift+Tab are confined to the
 * container. When `trapEnabled` is false the dialog is inert (a lower entry in
 * a modal stack) so it neither traps nor steals focus.
 *
 * On unmount: restores focus to whatever was focused before the dialog opened.
 * With stacked modals this means closing the top dialog returns focus to the
 * one beneath it (which captured that element when it opened).
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable]",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

/**
 * Move focus to the container's preferred / first focusable descendant, else
 * the container itself (`tabIndex={-1}`). Used on open and whenever a content
 * change removed the focused element (focus fell to `<body>`).
 */
export function focusFirstIn(container: HTMLElement): void {
  const focusable = getFocusable(container);
  const preferred = container.querySelector<HTMLElement>("[data-fg-initial-focus]");
  const target = preferred && focusable.includes(preferred) ? preferred : (focusable[0] ?? container);
  target.focus();
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  active: boolean,
  trapEnabled: boolean
): void {
  // Move focus into the dialog on open; restore it on close.
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Defer so portal children are laid out before we focus.
    const raf = requestAnimationFrame(() => focusFirstIn(container));

    return () => {
      cancelAnimationFrame(raf);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef]);

  // Confine Tab to the topmost (trap-enabled) dialog.
  useEffect(() => {
    if (!active || !trapEnabled) return;
    const container = containerRef.current;
    if (!container) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        // Nothing tabbable — keep focus pinned to the container.
        e.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || activeEl === container || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || activeEl === container || !container.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener("keydown", onKeyDown);
    return () => container.removeEventListener("keydown", onKeyDown);
  }, [active, trapEnabled, containerRef]);
}
